import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Clock, Flame, Dumbbell, Leaf, ChefHat,
  CheckCircle2, Loader2, Utensils, X
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const resolveImg = (url) => {
  if (!url) return "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800";
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

const DietBadge = ({ type }) => {
  if (!type) return null;
  const s = {
    Vegan: "bg-green-600 text-white",
    Veg: "bg-green-500 text-white",
    Egg: "bg-amber-500 text-white",
    "Non-Veg": "bg-red-500 text-white",
  };
  const d = { Vegan: "bg-green-200", Veg: "bg-green-200", Egg: "bg-amber-200", "Non-Veg": "bg-red-200" };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ${s[type] || s.Veg}`}>
      <span className={`w-2 h-2 rounded-sm ${d[type] || d.Veg}`} />
      {type}
    </span>
  );
};

const MealDetailPage = () => {
  const { mealId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [meal, setMeal] = useState(location.state?.meal || null);
  const [loading, setLoading] = useState(!meal);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const from = location.state?.from || "library";

  useEffect(() => {
    if (meal) return;
    const fetchMeal = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API}/meals/${mealId}`);
        if (res.data) setMeal(res.data);
        else toast.error("Meal not found");
      } catch (e) { toast.error("Failed to load meal"); }
      setLoading(false);
    };
    fetchMeal();
  }, [mealId, meal]);

  const goBack = () => {
    if (from === "home") navigate("/");
    else navigate("/library");
  };

  const assignToday = async (mealType) => {
    setSaving(true);
    const email = localStorage.getItem("userEmail");
    if (!email) { toast.error("Please login first"); setSaving(false); return; }

    try {
      const today = new Date().toISOString().split("T")[0];

      // Save to meal_history via API
      await axios.post(`${API}/history`, {
        user_email: email,
        meal_id: meal.meal_id,
        meal_name: meal.meal_name,
        meal_image: meal.image_url || "",
        meal_type: mealType,
        date: today
      });

      // Update localStorage plan
      const plansKey = "mealPlans";
      const plans = JSON.parse(localStorage.getItem(plansKey) || "{}");
      if (!plans[today]) plans[today] = {};
      plans[today][mealType.toLowerCase()] = {
        meal_id: meal.meal_id,
        meal_name: meal.meal_name,
        image_url: meal.image_url,
        calories: meal.calories,
        protein: meal.protein,
        prep_time_min: meal.prep_time_min,
        diet_type: meal.diet_type,
      };
      localStorage.setItem(plansKey, JSON.stringify(plans));

      toast.success(`${meal.meal_name} added to today's ${mealType}!`);
      setShowPicker(false);
      setSaving(false);

      // Navigate to Home
      setTimeout(() => navigate("/"), 800);
    } catch (e) {
      toast.error("Failed to save");
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!meal) {
    return (
      <div className="app-container py-16 text-center">
        <p className="text-muted-foreground">Meal not found</p>
        <Button variant="outline" onClick={goBack} className="mt-4">Go back</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-32" data-testid="meal-detail-page">
     <div className="max-w-md mx-auto">
      {/* Hero Image */}
      <div className="relative">
        <img src={resolveImg(meal.image_url)}
          alt={meal.meal_name} className="w-full aspect-[4/3] object-cover rounded-b-3xl sm:rounded-b-3xl"
          onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=800"; }} />

        {/* Back button */}
        <button onClick={goBack}
          className="absolute top-4 left-4 w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/60 transition-all"
          data-testid="meal-detail-back">
          <ArrowLeft size={18} />
        </button>

        {/* Diet badge on image */}
        <div className="absolute bottom-3 left-3 flex gap-1.5">
          <DietBadge type={meal.diet_type} />
          {meal.is_gluten_free && <span className="px-2 py-0.5 rounded bg-teal-500 text-white text-[11px] font-bold">Gluten-Free</span>}
        </div>
      </div>

      {/* Content */}
      <div className="px-5 -mt-4 relative">
        <div className="bg-white rounded-2xl shadow-lg border border-border/30 p-5">
          {/* Title */}
          <h1 className="text-xl font-bold mb-1" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="meal-detail-name">
            {meal.meal_name}
          </h1>
          {meal.notes && <p className="text-sm text-muted-foreground mb-3">{meal.notes}</p>}

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{meal.primary_meal_type}</span>
            {meal.secondary_meal_type && meal.secondary_meal_type !== "null" && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 border border-blue-200">Also: {meal.secondary_meal_type}</span>
            )}
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">{meal.base}</span>
            {meal.cuisine && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted/50 text-muted-foreground">{meal.cuisine}</span>}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 mb-5">
            <div className="bg-orange-50 rounded-xl p-3 text-center">
              <Flame size={18} className="mx-auto text-orange-500 mb-1" />
              <p className="text-base font-bold text-foreground">{meal.calories}</p>
              <p className="text-[10px] text-muted-foreground">kcal</p>
            </div>
            <div className="bg-blue-50 rounded-xl p-3 text-center">
              <Dumbbell size={18} className="mx-auto text-blue-500 mb-1" />
              <p className="text-base font-bold text-foreground">{meal.protein}g</p>
              <p className="text-[10px] text-muted-foreground">protein</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-3 text-center">
              <Clock size={18} className="mx-auto text-slate-500 mb-1" />
              <p className="text-base font-bold text-foreground">{meal.prep_time_min}</p>
              <p className="text-[10px] text-muted-foreground">minutes</p>
            </div>
          </div>

          {/* Ingredients */}
          {meal.ingredients && meal.ingredients.length > 0 && (
            <div className="mb-5">
              <h2 className="text-sm font-semibold mb-2.5 flex items-center gap-1.5">
                <Leaf size={14} className="text-green-600" />Ingredients
              </h2>
              <div className="space-y-1.5">
                {meal.ingredients.map((ing, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-primary/40 mt-1.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{ing}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Steps */}
          {meal.steps && meal.steps.length > 0 && (
            <div className="mb-4">
              <h2 className="text-sm font-semibold mb-2.5 flex items-center gap-1.5">
                <ChefHat size={14} className="text-amber-600" />How to make
              </h2>
              <div className="space-y-3">
                {meal.steps.map((step, i) => (
                  <div key={i} className="flex gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-background via-background to-transparent">
        <div className="max-w-md mx-auto">
          <Button onClick={() => setShowPicker(true)} className="w-full h-12 rounded-2xl text-sm font-semibold gap-2 shadow-lg" data-testid="make-today-btn">
            <Utensils size={16} />Make this today
          </Button>
        </div>
      </div>

      {/* Meal Type Picker Bottom Sheet */}
      {showPicker && (
        <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid="meal-picker-overlay">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setShowPicker(false)} />
          <div className="relative bg-white rounded-t-3xl w-full max-w-md p-6 pb-8 animate-slide-up shadow-xl" data-testid="meal-picker-sheet">
            <div className="w-10 h-1 bg-muted-foreground/20 rounded-full mx-auto mb-5" />
            <h3 className="text-base font-semibold text-center mb-1">Add to today's plan</h3>
            <p className="text-xs text-muted-foreground text-center mb-5">Which meal is this for?</p>

            <div className="space-y-2.5">
              {[
                { type: "Breakfast", icon: "🌅", desc: "Morning meal" },
                { type: "Lunch", icon: "☀️", desc: "Afternoon meal" },
                { type: "Dinner", icon: "🌙", desc: "Evening meal" },
              ].map(({ type, icon, desc }) => (
                <button key={type} onClick={() => assignToday(type)} disabled={saving}
                  className="w-full flex items-center gap-4 p-4 rounded-2xl border border-border/40 hover:border-primary/50 hover:bg-primary/5 transition-all text-left"
                  data-testid={`pick-${type.toLowerCase()}`}>
                  <span className="text-2xl">{icon}</span>
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{type}</p>
                    <p className="text-xs text-muted-foreground">{desc}</p>
                  </div>
                  {saving ? <Loader2 size={16} className="animate-spin text-primary" /> : <CheckCircle2 size={16} className="text-muted-foreground/30" />}
                </button>
              ))}
            </div>

            <button onClick={() => setShowPicker(false)}
              className="mt-4 w-full py-2.5 text-sm text-muted-foreground hover:text-foreground transition-colors text-center"
              data-testid="picker-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}
     </div>
    </div>
  );
};

export default MealDetailPage;
